import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/es';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Link2, AlertCircle, Calendar as CalIcon, User, UserCheck, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Lista de cirugías SIN Medilink vinculado en el periodo seleccionado.
 * Cada fila tiene un botón "Vincular" que abre el SurgeryDialog en modo edit.
 *
 * NO llama a Medilink API — solo GET /api/dashboard/unlinked-surgeries (local).
 */
export function UnlinkedSurgeriesDialog({ open, onClose, startDate, endDate, onEditSurgery }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/dashboard/unlinked-surgeries`, {
        withCredentials: true,
        params: { start_date: startDate, end_date: endDate },
      });
      setItems(data.surgeries || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [open, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col" data-testid="unlinked-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A1C18]">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            Cirugías sin vincular con Medilink
          </DialogTitle>
          <p className="text-xs text-[#6A736B]">
            {items.length} {items.length === 1 ? 'cirugía' : 'cirugías'} sin atención Medilink · Datos locales, sin consultar Medilink.
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6" data-testid="unlinked-list">
          {loading ? (
            <div className="py-10 text-center text-[#6A736B]">
              <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
              Cargando...
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-[#6A736B] italic">
              ¡Todo vinculado! No hay cirugías pendientes en este periodo.
            </div>
          ) : (
            <ul className="divide-y divide-[#F3EBE1]">
              {items.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center justify-between py-3 gap-3"
                  data-testid={`unlinked-row-${s.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1A1C18] truncate">
                      {s.patient_name || '(Sin nombre)'}
                      {s.patient_rut && (
                        <span className="text-[#6A736B] font-normal ml-2">· {s.patient_rut}</span>
                      )}
                    </p>
                    <p className="text-xs text-[#6A736B] flex items-center gap-3 mt-0.5 flex-wrap">
                      <span className="flex items-center gap-1"><CalIcon size={11} /> {moment(s.date).format('DD MMM YYYY')} {s.time}</span>
                      {s.doctor_name && <span className="flex items-center gap-1"><User size={11} /> {s.doctor_name}</span>}
                      {s.consultant_name && (
                        <span className="flex items-center gap-1" data-testid={`unlinked-consultant-${s.id}`}>
                          <UserCheck size={11} /> {s.consultant_name}
                        </span>
                      )}
                      {s.pavilion && <span>· {s.pavilion}</span>}
                    </p>
                    {s.surgery_type && (
                      <p className="text-xs text-[#8A9280] mt-0.5 truncate">{s.surgery_type}</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => onEditSurgery?.(s)}
                    className="bg-[#2B4C3B] hover:bg-[#3A604D] text-white flex-shrink-0"
                    data-testid={`unlinked-link-btn-${s.id}`}
                  >
                    <Link2 size={14} className="mr-1.5" /> Vincular
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter className="gap-2 pt-3 border-t border-[#F3EBE1]">
          <Button variant="outline" size="sm" onClick={load} data-testid="unlinked-refresh">
            <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button variant="outline" onClick={onClose} data-testid="unlinked-close">
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
